import styled from '@emotion/styled';
import type { AvatarResponse } from '@innovationdepartment/proxima-sdk-axios';
import { Banner, Button, Text } from '@innovationdepartment/proxima-ui';
import { Pagination } from 'ui/Pagination';
import type { GetDisplay } from '../useMetadataDisplay';
import AvatarCard from './AvatarCard';
import AvatarGrid from './AvatarGrid';
import AvatarFilterSidebar from './Filters/AvatarFilterSidebar';
import type { AgeRange, AvatarSortKey } from './useAvatarFilters';

const SectionWrapper = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const PageLayout = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
`;
const LeftSidebar = styled.aside`
  width: 260px;
  flex-shrink: 0;
`;
const MainContent = styled.div`
  flex: 1;
  min-width: 0;
`;
const CenteredState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 64px 0;
`;
type AvatarsViewProps = {
  loading: boolean;
  loadError: string | null;
  /** All library avatars, before filtering. */
  totalCount: number;
  /** The current page of avatars, after the age/gender filters and sort. */
  avatars: AvatarResponse[];
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  getDisplay: GetDisplay;
  ageBounds: AgeRange;
  ageRange: AgeRange;
  onAgeRangeChange: (range: AgeRange) => void;
  genderOptions: Array<{ value: string; display: string }>;
  selectedGenders: string[];
  onToggleGender: (gender: string) => void;
  sortKey: AvatarSortKey;
  onSortChange: (sortKey: AvatarSortKey) => void;
  onRetry: () => void;
};
const AvatarsView = ({
  loading,
  loadError,
  totalCount,
  avatars,
  totalPages,
  currentPage,
  onPageChange,
  getDisplay,
  ageBounds,
  ageRange,
  onAgeRangeChange,
  genderOptions,
  selectedGenders,
  onToggleGender,
  sortKey,
  onSortChange,
  onRetry
}: AvatarsViewProps) => {
  // While loading, the global overlay spinner (useShowSpinner in the container) is visible
  if (loading) return null;
  if (loadError) {
    return (
      <CenteredState>
        <Banner type="error" title="Unable to load avatars" body={loadError} />
        <Button label="Retry" size="small" onClick={onRetry} />
      </CenteredState>
    );
  }
  return (
    <SectionWrapper>
      <SectionHeader>
        <Text variant="h5">{`Avatars (${totalCount})`}</Text>
      </SectionHeader>
      {totalCount === 0 ? (
        <Text variant="body1" color="neutral70">
          The avatar library is empty.
        </Text>
      ) : (
        <PageLayout>
          <LeftSidebar>
            <AvatarFilterSidebar
              ageBounds={ageBounds}
              ageRange={ageRange}
              onAgeRangeChange={onAgeRangeChange}
              genderOptions={genderOptions}
              selectedGenders={selectedGenders}
              onToggleGender={onToggleGender}
              sortKey={sortKey}
              onSortChange={onSortChange}
            />
          </LeftSidebar>
          <MainContent>
            {avatars.length === 0 ? (
              <Text variant="body1" color="neutral70">
                No avatars match your filters.
              </Text>
            ) : (
              <>
                <AvatarGrid>
                  {avatars.map(avatar => (
                    <AvatarCard key={avatar.id} avatar={avatar} getDisplay={getDisplay} />
                  ))}
                </AvatarGrid>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                />
              </>
            )}
          </MainContent>
        </PageLayout>
      )}
    </SectionWrapper>
  );
};
export default AvatarsView;
